import { CalendarOutlined } from '@ant-design/icons';
import { Avatar } from 'antd';
import { format } from 'date-fns';
import React from 'react';
import { CopyToClipboard } from 'src/components';
import { formatPhoneNumber } from 'src/utils';
import { generateColorFromName, isValidEmail } from 'src/utils/misc';

type PatientDetailsBarProps = {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  id: string | number;
  dob: string;
  email?: string;
  phone?: string;
  ehrId?: string | null;
  pastAppointment?: string | null;
  nextAppointment?: string | null;
  extras?: React.ReactNode;
};

const getBulletSeperatedNodes = (nodes: React.ReactNode[]) => {
  if (!nodes) return [];

  return nodes.filter(Boolean).reduce((acc: React.ReactNode[], curr) => {
    if (acc.length) {
      acc.push(<>&nbsp;•&nbsp;</>);
    }
    if (curr) {
      acc.push(curr);
    }
    return acc;
  }, [] as React.ReactNode[]);
};

export const PatientDetailsBar: React.FC<PatientDetailsBarProps> = ({
  fullName,
  firstName,
  lastName,
  id,
  dob,
  email,
  phone,
  ehrId,
  pastAppointment,
  nextAppointment,
  extras,
}) => {
  const parts = fullName?.split(' ') || [firstName, lastName];
  const name = parts.filter(Boolean).join(' ');

  const patientAttributes = getBulletSeperatedNodes([
    dob && `DOB: ${format(new Date(dob), 'MM/dd/yyyy')}`,
    email && isValidEmail(email) && <div>Email: {email}</div>,
    phone && <div>Phone: {formatPhoneNumber(phone)}</div>,
    ehrId && <div>EHR/MRN ID: {ehrId}</div>,
  ]);

  const appointmentAttributes = getBulletSeperatedNodes([
    pastAppointment && new Date(pastAppointment) < new Date() && (
      <div className="flex items-center gap-1 text-gray-500">
        <CalendarOutlined />
        Past appt.: {format(new Date(pastAppointment), 'MM/dd/yyyy')}
      </div>
    ),
    nextAppointment && new Date(nextAppointment) > new Date() && (
      <div className="flex items-center gap-1 text-[#00d090]">
        <CalendarOutlined />
        Next appt: {format(new Date(nextAppointment), 'MM/dd/yyyy')}
      </div>
    ),
  ]);

  return (
    <div className={`flex items-center gap-2 px-4 py-2`}>
      <div className="flex-shrink-0">
        <Avatar
          style={{
            backgroundColor: generateColorFromName(name),
            color: '#ffffff',
          }}
        >
          {name
            .split(' ')
            .map((name) => name[0]?.toUpperCase())
            .join('')
            .substring(0, 2)}
        </Avatar>
      </div>
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <div className="text-lg font-medium">{name}</div>
          <div className="text-sm text-gray-500">
            <CopyToClipboard text={id.toString()} hasIcon={false}>
              ID: {id}
            </CopyToClipboard>
          </div>
        </div>
        <div className="text-sm text-gray-500 flex mb-[2px]">
          {patientAttributes.map((attr, index) => (
            <div key={index}>{attr}</div>
          ))}
        </div>
        <div className="flex">
          {appointmentAttributes.map((attr, index) => (
            <div key={index}>{attr}</div>
          ))}
        </div>
      </div>
      <div className="right-0 ml-auto flex items-center">{extras}</div>
    </div>
  );
};
